import type { Fetcher } from '@remix-run/react';
import { useFetchers, useSearchParams } from '@remix-run/react';
import { useEffect, useState } from 'react';
import type { PAGE_POSITIONS } from 'types';
import type { Image, ProductLevelStockStatus } from 'types/capi/shared';

import FlameIcon from '~/assets/icons/Flame';
import NotifyMeBell from '~/assets/icons/NotifyMeBell';
import ClickNotificationEvent from '~/events/ClickNotificationEvent';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import type { User } from '~/rootLoader';

import Modals from './Modals';
import Tracked from './Tracked';

enum FetchState {
  Idle,
  Setting,
  Unsetting
}

function getFetchState(product: NotificationData, fetchers: Fetcher[]) {
  const url = `/bolt/notification/${product.legacyId}`;
  const fetcher = fetchers.find((f) => f.formAction?.startsWith(url));

  if (!fetcher) return FetchState.Idle;

  return fetcher?.formData?.get('delete')
    ? FetchState.Unsetting
    : FetchState.Setting;
}

function getIsNotified(
  state: FetchState,
  product: NotificationData,
  user: User
) {
  if (state !== FetchState.Idle) return state === FetchState.Setting;

  return Boolean(
    user.actions?.alerts?.products?.some((p) => product.legacyId === p.id)
  );
}

export type NotificationData = {
  affiliateName?: string;
  id: number;
  image?: Image;
  legacyId: string;
  name: string;
  price: number | null;
  queryID?: string;
  stockStatus?: ProductLevelStockStatus;
  weeklyNotificationsCount?: number;
};

type MainProps = {
  buttonStyle: 'compact' | 'normal';
  className?: string;
  openModalViaQueryString?: boolean;
  pagePosition: PAGE_POSITIONS | `TEMP_${string}`;
  product: NotificationData;
  showTooltip?: boolean;
};

const ProductNotificationButton = ({
  product,
  buttonStyle,
  className,
  pagePosition,
  showTooltip = false,
  openModalViaQueryString = false
}: MainProps) => {
  const user = useUser();
  const fetchers = useFetchers();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchState = getFetchState(product, fetchers);
  const isNotified = getIsNotified(fetchState, product, user);
  const { isBot } = useRootData();

  useEffect(() => {
    if (
      (searchParams.has('modal') ||
        searchParams.get('notify') === product.legacyId) &&
      openModalViaQueryString
    )
      setIsModalOpen(true);
  }, [product, searchParams, openModalViaQueryString]);

  function onClick(e: React.SyntheticEvent) {
    e.preventDefault();
    e.stopPropagation();

    setIsModalOpen(true);

    return new ClickNotificationEvent({
      buttonName: 'OPEN_NOTIFICATION',
      isBot,
      pagePosition: pagePosition,
      productId: product.id,
      productName: product.name,
      queryID: product.queryID ?? localStorage.getItem('queryID'),
      user
    }).send();
  }

  function onModalDismiss() {
    if (
      searchParams.has('modal') ||
      searchParams.has('notify') ||
      searchParams.has('registrationSuccessful')
    ) {
      setSearchParams((params) => {
        params.delete('modal');
        params.delete('notify');
        params.delete('registrationSuccessful');

        return params;
      });
    }

    setIsModalOpen(false);
  }

  return (
    <div className={className}>
      {buttonStyle === 'compact' ? (
        <CompactButton
          isNotified={isNotified}
          isDisabled={fetchState !== FetchState.Idle}
          onClick={onClick}
          className={className}
          pagePosition={pagePosition}
        />
      ) : (
        <NormalButton
          isNotified={isNotified}
          isDisabled={fetchState !== FetchState.Idle}
          showTooltip={showTooltip}
          onClick={onClick}
          product={product}
          className={className}
          pagePosition={pagePosition}
        />
      )}
      <Modals.ProductNotification
        product={product}
        isOpen={isModalOpen}
        onDismiss={onModalDismiss}
      />
    </div>
  );
};

type ButtonProps = {
  className?: string;
  isDisabled: boolean;
  isNotified: boolean;
  pagePosition: MainProps['pagePosition'];
  showTooltip?: boolean;
} & React.HTMLProps<HTMLButtonElement>;

const CompactButton = ({
  onClick,
  isDisabled,
  pagePosition,
  isNotified
}: ButtonProps) => {
  return (
    <Tracked.Button
      className="transition-transform duration-200 ease-in-out hover:scale-125"
      onClick={onClick}
      disabled={isDisabled}
      type="submit"
      data-cy="product-notification-button-compact"
      name="OPEN_NOTIFICATION"
      pagePosition={pagePosition}
      variant="none"
    >
      <NotifyMeBell variant={isNotified ? 'filled' : 'thin'} />
    </Tracked.Button>
  );
};

const NormalButton = ({
  product,
  isNotified,
  showTooltip,
  isDisabled,
  onClick,
  pagePosition,
  className
}: ButtonProps & { product: NotificationData }) => {
  return (
    <Tracked.Button
      disabled={isDisabled}
      type="submit"
      className="flex overflow-hidden w-full"
      onClick={onClick}
      data-cy="product-notification-button-normal"
      name="OPEN_NOTIFICATION"
      pagePosition={pagePosition}
      variant="none"
    >
      {showTooltip && !isNotified && <Tooltip product={product} />}
      <div className={getNormalButtonStyles(isNotified, className)}>
        <NotifyMeBell variant={isNotified ? 'filled' : 'thick'} />
        <span className="flex items-center font-semibold tracking-wide">
          {isNotified ? 'Notified' : 'Notify me'}
        </span>
      </div>
    </Tracked.Button>
  );
};

const Tooltip = ({ product }: { product: NotificationData }) => {
  const { stockStatus, weeklyNotificationsCount = 0 } = product;

  return (
    <div className="bg-purple-hola/5 text-purple-hola self-stretch opacity-0 flex animate-slide-from-right whitespace-nowrap items-center -mr-2 pl-2 pr-4 z-10">
      {weeklyNotificationsCount >= 50 ? (
        <div className="gap-1 whitespace-nowrap flex">
          <FlameIcon width={16} height={16} />
          {formatNumberToK(weeklyNotificationsCount)}
          <span className="hidden sm:block">notifications</span> set in the last
          7 days
        </div>
      ) : (
        <>
          {['in-stock', 'reseller'].includes(stockStatus!) &&
            'Get notified of price drops'}
          {['coming-soon', 'tbc'].includes(stockStatus!) &&
            'Get a release alert'}
          {stockStatus === 'sold-out' && 'Get a restock alert'}
        </>
      )}
    </div>
  );
};

function formatNumberToK(num: number) {
  const formatted = num > 1000 ? (num / 1000).toFixed(1) + 'k' : `${num}`;

  return formatted.includes('.0') ? formatted.replace('.0', '') : formatted;
}

function getNormalButtonStyles(isNotified: boolean, className: string = '') {
  return `flex w-full items-center justify-center gap-2 rounded-lg p-2 transition duration-200 ease-in-out z-20 ${className} ${
    isNotified
      ? 'border border-purple-hola text-purple-hola'
      : 'bg-purple-hola text-white '
  }`;
}

export default ProductNotificationButton;
